/**
 * Record-contact-outcome drawer (COC-STATION-V1, V4-P1). Acts IN PLACE (the
 * 360 law: acting on a record never forces navigation away): pick the
 * config-backed outcome (provisional TEST/DEFAULT vocabulary pending tenant
 * Q5/Q6 -- badged), the channel, and -- when the outcome allows/requires
 * it -- the callback instant in CLINIC TIME (IST), converted through the ONE
 * IST wall-clock converter (lib/datetime-ist; never the browser timezone --
 * the R-41 class is not repeated here).
 *
 * Writes through the EXISTING lead-events writer (POST /api/coc/lead-events,
 * disposition extras). No stage write, no lead mutation. Server 400/409
 * messages render verbatim (ERROR-PASSTHROUGH law).
 */

import { useMemo, useState } from 'react';
import DrawerShell from '@/dashboard/ui/DrawerShell';
import {
  recordContactOutcome,
  type CocContactOutcomeView,
  type CocStationRowView,
} from '@/dashboard/api';
import { istWallClockToUtcIso, isWallClockValue } from '@/lib/datetime-ist';

const CHANNELS: readonly { key: string; label: string }[] = [
  { key: 'phone', label: 'Phone' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
  { key: 'in_person', label: 'In person' },
  { key: 'other', label: 'Other' },
];

export interface RecordOutcomeDialogProps {
  readonly row: CocStationRowView;
  readonly outcomes: readonly CocContactOutcomeView[];
  readonly onClose: () => void;
  /** Refetch hook after a successful write (the station re-derives). */
  readonly onRecorded: () => void;
}

export default function RecordOutcomeDialog({
  row,
  outcomes,
  onClose,
  onRecorded,
}: RecordOutcomeDialogProps) {
  const [outcomeId, setOutcomeId] = useState<string>('');
  const [channel, setChannel] = useState<string>('phone');
  const [callbackLocal, setCallbackLocal] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = useMemo(
    () => outcomes.find((o) => o.id === outcomeId) ?? null,
    [outcomes, outcomeId],
  );
  const anyProvisional = outcomes.some((o) => o.provisional);
  const callbackMode = selected?.callback ?? 'none';
  const callbackNeeded = callbackMode === 'required';
  const callbackAllowed = callbackMode !== 'none';

  const canSubmit =
    !submitting &&
    selected !== null &&
    (!callbackNeeded || isWallClockValue(callbackLocal));

  const submit = () => {
    if (selected === null || submitting) return;
    if (callbackNeeded && !isWallClockValue(callbackLocal)) {
      setError('Pick the callback date and time (clinic time, IST).');
      return;
    }
    setSubmitting(true);
    setError(null);
    const callbackAtIso =
      callbackAllowed && isWallClockValue(callbackLocal)
        ? istWallClockToUtcIso(callbackLocal)
        : undefined;
    void recordContactOutcome({
      leadId: row.leadId,
      eventType: selected.eventType,
      outcomeId: selected.id,
      channel,
      ...(callbackAtIso !== undefined ? { callbackAtIso } : {}),
    })
      .then(() => {
        onRecorded();
        onClose();
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
        setSubmitting(false);
      });
  };

  return (
    <DrawerShell
      eyebrow={row.displayName || 'Lead'}
      title="Record contact outcome"
      onClose={onClose}
      testId="coc-record-outcome"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="ac-btn ac-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ac-btn ac-btn--primary"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="record-outcome-submit"
          >
            {submitting ? 'Recording...' : 'Record outcome'}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="record-outcome-body">
        {anyProvisional ? (
          <p
            className="text-xs text-muted-foreground"
            data-testid="outcome-provisional-note"
          >
            Provisional vocabulary (TEST/DEFAULT) pending tenant confirmation
            (Q5/Q6) - labels may change; recorded outcomes are kept.
          </p>
        ) : null}

        <label className="coc-station__field">
          <span className="coc-station__field-label">
            Outcome <span aria-hidden="true">*</span>
          </span>
          <select
            value={outcomeId}
            onChange={(e) => {
              setOutcomeId(e.target.value);
              setError(null);
            }}
            data-testid="outcome-select"
          >
            <option value="">Select an outcome...</option>
            {outcomes.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
                {o.provisional ? ' (provisional)' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="coc-station__field">
          <span className="coc-station__field-label">Channel</span>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            data-testid="outcome-channel"
          >
            {CHANNELS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        {callbackAllowed ? (
          <label className="coc-station__field">
            <span className="coc-station__field-label">
              Callback {callbackNeeded ? <span aria-hidden="true">*</span> : '(optional)'}
            </span>
            <input
              type="datetime-local"
              value={callbackLocal}
              onChange={(e) => setCallbackLocal(e.target.value)}
              data-testid="outcome-callback-input"
            />
            <span className="text-xs text-muted-foreground">
              Clinic time - IST (Asia/Kolkata)
            </span>
          </label>
        ) : null}

        {error !== null ? (
          <p className="coc-station__error" role="alert" data-testid="outcome-error">
            {error}
          </p>
        ) : null}
      </div>
    </DrawerShell>
  );
}
